/**
 * Machines table, used in machines view page and home page.
 *
 * @category components
 * @module machine-listing
 */

import { ReactNode } from "react";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";

import { useMachines, Machine, useMachinesByOrgs } from "_/data/machines";
import {
  Attempt,
  Project,
  Revision,
  useLastAttemptForEachMachine,
  useProjects,
} from "_/data/projects";
import { Material, useMaterials } from "_/data/materials";
import { Org } from "_/data/orgs";
import {
  useCurrentUserPermissions,
  ROOT_DOMAIN,
  Permission,
} from "_/data/rbac";
import {
  useChannelNameToBuildset,
  ChannelNameToBuildset,
} from "_/data/channels";
import {
  useBuildsets,
  buildBuildsetIdMap,
  BuildsetIdMap,
} from "_/data/buildsets";

import {
  Table,
  ColumnDef,
  CustomCellLink,
  CustomCellStatic,
} from "_/components/table";
import { DateOnly, RelativeDateTime } from "_/components/date-time";
import { MenuItem, OverlayMenu } from "_/components/overlay-menu";
import { Button } from "_/components/button";
import { Icon } from "_/components/icon";
import { MaterialsList } from "_/components/materials";
import { AttemptCell } from "_/components/jobs-listing";
import { MachineStatusBadge } from "_/components/machine-header";

import { machineUrls, routeUrls } from "_/routes";

import { indexById } from "_/utils";

import { SoftwareVersionCell } from "./softwareVersionCell";
import { ChannelCell } from "./channelCell";

import * as S from "./styled";

interface AdminMachineListingProps {
  /** Which org(s) to show machines for. */
  orgs?: Org[];

  /** Adds custom actions to the actions menu. */
  actions?: (machine: Machine) => MenuItem[];

  /** Whether to show the org column. */
  showOrg: boolean;

  /**
   * Whether to include only detached machines. If not truthy, then only show
   * attached machines.
   * */
  detached?: boolean;

  /** Allows filtering machines based on custom criteria. */
  filter?: (machine: Machine, org: Org) => boolean;
}

const ActionsMenu = ({
  machine,
  actions,
}: {
  machine: Machine;
  actions?: (machine: Machine) => MenuItem[];
}) => {
  const menuItems = actions
    ? actions(machine)
    : [
        {
          render: t`common.support`,
          // TODO: this menu item should open a modal with a link to the
          // support email and a checkbox to share this machine's data
          // with AON3D support.
          onClick: () => {},
        },
      ];
  return (
    <OverlayMenu
      target={
        <Button kind="secondary" size="small" iconOnly>
          <Icon color="#f28c28" variant="MoreVert" />
        </Button>
      }
      placement="bottomStart"
      items={menuItems}
    />
  );
};

// The table renders each `cell` function as a React component. Recreating
// columns on machine refreshes changes those component identities and remounts
// the cells. Put changing values on rows so stable renderers read fresh data.
type MachineTableRow = Machine & {
  tableUpdatedAt: number;
  tableActions?: (machine: Machine) => MenuItem[];
};

const machineColumnDefs: Record<string, ColumnDef<MachineTableRow>> = {
  name: {
    accessorKey: "name",
    header: () => t`common.machine`,
    meta: { customTd: true },
    cell: ({ row: { original: rowData } }) => (
      <CustomCellLink to={machineUrls.index(rowData.id)}>
        {rowData.name}
      </CustomCellLink>
    ),
  },
  serial: {
    accessorKey: "hardware.serial",
    header: () => t`common.machine-serial`,
    cell: ({ row: { original: rowData } }) =>
      rowData.hardwareAttached ? (
        <div>{rowData.hardware.serial}</div>
      ) : (
        <S.DetachedSerialNumberContainer>
          <S.DetachedSerialNumber>
            {rowData.hardware.serial}{" "}
          </S.DetachedSerialNumber>
          <S.DetachedSerialNumberLabel>
            ({t`common.detached`})
          </S.DetachedSerialNumberLabel>
        </S.DetachedSerialNumberContainer>
      ),
  },
  manufactureDate: {
    accessorKey: "hardware.manufactureDate",
    header: () => t`common.machine-manufacture-date`,
    cell: ({ row: { original: rowData } }) => (
      <DateOnly value={rowData.hardware.manufactureDate} />
    ),
  },
};

export type MachineLastSeenKind = "now" | "stale" | "offline" | "never";

export const calculateMachineLastSeenKind = ({
  machine,
  updatedAt,
}: {
  /** When the server last heard from the machine */
  machine: Machine;
  /** When we've gotten the latest info from the server */
  updatedAt: number;
}): MachineLastSeenKind => {
  const lastSeenDayjs = machine.lastSeen ? dayjs(machine.lastSeen) : null;
  const staleTime = dayjs(updatedAt).subtract(1, "minutes");
  const offlineTime = dayjs(updatedAt).subtract(15, "minutes");

  if (lastSeenDayjs === null || lastSeenDayjs === undefined) {
    return "never";
  } else if (lastSeenDayjs?.isBefore(offlineTime)) {
    return "offline";
  } else if (lastSeenDayjs?.isBefore(staleTime)) {
    return "stale";
  } else {
    return "now"; // seen within 1 minute
  }
};

/**
 * Show the relative time that this machine last contacted the server, and a
 * small icon indicating how long it's been.
 * */
export const MachineLastSeen = ({
  machine,
  updatedAt,
}: {
  machine: Machine;
  /** When we've gotten the latest info from the server */
  updatedAt: number;
}) => {
  const lastSeenKind = calculateMachineLastSeenKind({ machine, updatedAt });

  const lastSeenColors = {
    now: "green",
    offline: "red",
    stale: "orange",
    never: "grey",
  } as const;

  return (
    <S.LastSeenCell>
      <S.LastSeenDot variant="Circle" $color={lastSeenColors[lastSeenKind]} />
      <RelativeDateTime
        value={machine.lastSeen}
        isNow={lastSeenKind === "now"}
        defaultValue={t`components.machine-listing.never`}
      />
    </S.LastSeenCell>
  );
};

const orgLinkColumnDef: ColumnDef<MachineWithOrg> = {
  accessorKey: "org",
  header: () => t`common.org`,
  meta: { customTd: true },
  cell: ({ row: { original: rowData } }) => (
    <CustomCellLink to={routeUrls.admin.organization(rowData.org.id)}>
      {rowData.org.name}
    </CustomCellLink>
  ),
};

const lastSeenColumnDef: ColumnDef<MachineTableRow> = {
  accessorKey: "lastSeen",
  header: () => t`common.machine-last-seen`,
  sortingFn: (a, b) => {
    const aLastSeen = new Date(a.original.lastSeen ?? 0).getTime();
    const bLastSeen = new Date(b.original.lastSeen ?? 0).getTime();
    const aSerial = a.original.hardware.serial;
    const bSerial = b.original.hardware.serial;

    // If neither machine has ever reported in, sort by serial to ensure a
    // stable order.
    if (aLastSeen === 0 && bLastSeen === 0) {
      return aSerial.localeCompare(bSerial);
    }

    // Healthy machines on healthy networks report to Basis every few seconds.
    // Because different machines' reporting times are effectively random
    // within those intervals, if we purely sort by `lastSeen` then machines
    // frequently change places in the list without any meaningful difference.
    // To avoid this, recently-seen machines (which show up as "Now" in UI)
    // will be sorted by serial number. This ensures a stable sort order for
    // healthy, connected machines while still sorting by lastSeen for stale
    // (1-15 mins ago) or offline (>15 mins ago) machines.
    const aLastSeenKind = calculateMachineLastSeenKind({
      machine: a.original,
      updatedAt: a.original.tableUpdatedAt,
    });
    const bLastSeenKind = calculateMachineLastSeenKind({
      machine: b.original,
      updatedAt: b.original.tableUpdatedAt,
    });
    if (aLastSeenKind === "now" && bLastSeenKind === "now") {
      return aSerial.localeCompare(bSerial);
    }

    // Otherwise, just sort by the last seen timestamp.
    return aLastSeen - bLastSeen;
  },
  cell: ({ row: { original: rowData } }) => (
    <MachineLastSeen machine={rowData} updatedAt={rowData.tableUpdatedAt} />
  ),
};

const lastAttemptMaterialsColumnDef: ColumnDef<MachineWithLastAttempt> = {
  id: "lastAttemptMaterials",
  header: () => t`common.last-attempt-materials`,
  cell: ({ row: { original: rowData } }) =>
    rowData.lastAttempt ? (
      <MaterialsList materials={rowData.lastAttemptMaterials} />
    ) : null,
};

const lastAttemptColumnDef: ColumnDef<MachineWithLastAttempt> = {
  id: "lastAttempt",
  header: () => t`common.last-print-job`,
  meta: { customTd: true },
  cell: ({ row: { original: rowData } }) => {
    if (
      !rowData.lastAttempt ||
      !rowData.lastAttemptProject ||
      !rowData.lastAttemptRevision
    ) {
      return <CustomCellStatic />;
    }
    return (
      <AttemptCell
        project={rowData.lastAttemptProject}
        revision={rowData.lastAttemptRevision}
        attempt={rowData.lastAttempt}
      />
    );
  },
};

const machineStatusColumnDef: ColumnDef<MachineTableRow> = {
  id: "status",
  header: () => t`common.status`,
  cell: ({ row: { original: rowData } }) => (
    <MachineStatusBadge machine={rowData} updatedAt={rowData.tableUpdatedAt} />
  ),
};

const actionsColumnDef: ColumnDef<MachineTableRow> = {
  id: "actions",
  header: () => t`common.actions`,
  cell: ({ row: { original: rowData } }) => (
    <ActionsMenu machine={rowData} actions={rowData.tableActions} />
  ),
};

const softwareVersionColumnDef: ColumnDef<MachineWithOrg> = {
  accessorKey: "softwareVersion",
  header: () => t`common.software-version`,
  meta: { customTd: true },
  cell: ({ row: { original: rowData } }) => {
    const { userPermissions, channelNameToBuildset, buildsetIdMap } = rowData;
    return (
      <SoftwareVersionCell
        rowData={rowData}
        userPermissions={userPermissions}
        channelNameToBuildset={channelNameToBuildset}
        buildsetIdMap={buildsetIdMap}
      />
    );
  },
};

const channelColumnDef: ColumnDef<MachineWithOrg> = {
  accessorKey: "channel",
  header: () => t`common.channel`,
  meta: { customTd: true },
  cell: ({ row: { original: rowData } }) => {
    const { userPermissions } = rowData;
    return <ChannelCell rowData={rowData} userPermissions={userPermissions} />;
  },
};

const NoMachinesMessage = ({ message }: { message?: string }) => (
  <S.NoMachinesContainer>
    {message ?? t`components.machine-listing.no-machines`}
  </S.NoMachinesContainer>
);

const defaultMachineListSort = [{ id: "lastSeen", desc: true }];

type MachineWithLastAttempt = MachineTableRow & {
  lastAttempt: Attempt | undefined;
  lastAttemptRevision: Revision | undefined;
  lastAttemptProject: Project | undefined;
  lastAttemptMaterials: Material[] | undefined;
};

export const FullMachineListing = (): ReactNode => {
  return <NonAdminMachineListing view="full" />;
};

export const FullDetachedMachineListing = (): ReactNode => {
  return <NonAdminMachineListing view="full" detached />;
};

export const PartialMachineListing = (): ReactNode => {
  return <NonAdminMachineListing view="partial" />;
};

const NonAdminMachineListing = ({
  view,
  detached = false,
}: {
  view: "full" | "partial";
  detached?: boolean;
}): ReactNode => {
  const {
    data: unfilteredMachines = [],
    dataUpdatedAt: updatedAt,
    isLoading: machinesLoading,
  } = useMachines();
  const { data: materials = [], isLoading: materialsLoading } = useMaterials();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: attemptsByMachine = {}, isLoading: lastAttemptsLoading } =
    useLastAttemptForEachMachine();

  const isLoading =
    machinesLoading ||
    projectsLoading ||
    materialsLoading ||
    lastAttemptsLoading;

  // If there's only one machine (typical of customer orgs) then show the
  // machine even if it's not attached to hardware. For orgs with multiple
  // machines (which for the near future will be only AON3D orgs) only show
  // machines that are attached to hardware, unless it's the special "detached
  // machines" table on the machine list page.
  const machines =
    unfilteredMachines.length === 1
      ? unfilteredMachines
      : unfilteredMachines.filter((m) => {
          return detached ? !m.hardwareAttached : m.hardwareAttached;
        });

  const revisions = projects.flatMap((p) => p.revisions) ?? [];

  const materialByKey = indexById(materials);
  const projectsByKey = indexById(projects);
  const revisionsByKey = indexById(revisions);

  const machinesWithLastAttempt: MachineWithLastAttempt[] = machines.map(
    (machine) => {
      const lastAttempt = attemptsByMachine[machine.id];
      const lastAttemptRevision =
        lastAttempt && revisionsByKey[lastAttempt.revisionId];
      const lastAttemptProject =
        lastAttemptRevision && projectsByKey[lastAttemptRevision.projectId];
      const lastAttemptMaterials = lastAttemptRevision?.materials
        .map((id, i) => {
          const material = id ? materialByKey[id] : undefined;
          if (!material) {
            if (i === 0 && !id) {
              throw new Error(
                `Missing primary material in revision ${lastAttemptRevision.id}`
              );
            }
          }
          return material;
        })
        .filter(Boolean);

      const result = {
        ...machine,
        tableUpdatedAt: updatedAt,
        lastAttempt,
        lastAttemptRevision,
        lastAttemptProject,
        lastAttemptMaterials,
      };
      return result;
    }
  );

  // The cast below from `MachineTableRow` to `MachineWithLastAttempt` cols is OK
  // because `MachineWithLastAttempt` contains all the fields of `MachineTableRow`.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const columns = (
    view === "full"
      ? [
          machineColumnDefs.name,
          machineStatusColumnDef,
          lastSeenColumnDef,
          lastAttemptColumnDef,
          lastAttemptMaterialsColumnDef,
          actionsColumnDef,
        ]
      : [machineColumnDefs.name, machineStatusColumnDef]
  ) as ColumnDef<MachineWithLastAttempt>[];

  return (
    <Table
      columns={columns}
      data={machinesWithLastAttempt}
      loading={isLoading}
      noDataLabel={t`components.machine-listing.no-machines`}
      initialState={{ sorting: defaultMachineListSort }}
    />
  );
};

export interface MachineWithOrg extends MachineTableRow {
  org: Org;
  userPermissions: Permission[];
  channelNameToBuildset: ChannelNameToBuildset;
  buildsetIdMap: BuildsetIdMap;
}

export const AdminMachineListing = ({
  orgs,
  actions,
  showOrg,
  detached,
  filter,
}: AdminMachineListingProps): ReactNode => {
  const results = useMachinesByOrgs(orgs);
  const { data: userPermissions } = useCurrentUserPermissions(ROOT_DOMAIN);
  const { data: buildsets = [] } = useBuildsets();
  const channelNameToBuildset = useChannelNameToBuildset();
  const buildsetIdMap = buildBuildsetIdMap(buildsets);

  const machinesAugmented = (results.data ?? []).map((machineAndOrg) => {
    // Only show detached machines in the detached-only table, and
    // don't show detached machines anywhere else.
    const isDesiredDetachedState =
      detached === !machineAndOrg.machine.hardwareAttached;

    // If all machines are excluded by the user's filters, then we'll render a
    // "no matches" message below instead of "no machines" which is what we show
    // for a table with no machines at all that match the detached or
    // not-detached state requested by the caller.
    const excludedByFilter =
      isDesiredDetachedState &&
      filter &&
      !filter?.(machineAndOrg.machine, machineAndOrg.org);

    return {
      machineAndOrg,
      isDesiredDetachedState,
      excludedByFilter,
    };
  });
  const machinesWithOrgs: MachineWithOrg[] = machinesAugmented
    .filter((mo) => mo.isDesiredDetachedState && !mo.excludedByFilter)
    .map((mo) => ({
      ...mo.machineAndOrg.machine,
      org: mo.machineAndOrg.org,
      tableUpdatedAt: results.dataUpdatedAt,
      tableActions: actions,
      userPermissions,
      channelNameToBuildset,
      buildsetIdMap,
    }));

  const isLoading = results.isLoading;

  const { serial, manufactureDate, name } = machineColumnDefs;

  // `MachineWithOrg` includes all fields of `MachineTableRow`, so its cells can
  // safely use the shared columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const columns = [
    showOrg ? orgLinkColumnDef : undefined,
    name,
    manufactureDate,
    serial,
    machineStatusColumnDef,
    lastSeenColumnDef,
    softwareVersionColumnDef,
    channelColumnDef,
    actionsColumnDef,
  ].filter(Boolean) as ColumnDef<MachineWithOrg>[];

  if (!isLoading && !machinesWithOrgs.length)
    return (
      <NoMachinesMessage
        message={
          filter && machinesAugmented.some((mo) => mo.excludedByFilter)
            ? t`components.machine-listing.no-matches`
            : undefined
        }
      />
    );

  return (
    <>
      {detached && machinesWithOrgs.length && (
        <S.DetachedAlert kind="info">
          {t`components.machine-listing.detached-description`}
        </S.DetachedAlert>
      )}
      <Table
        columns={columns}
        data={machinesWithOrgs}
        loading={isLoading}
        initialState={{
          sorting: defaultMachineListSort,
        }}
      />
    </>
  );
};
